/* eslint-disable import/no-unused-modules */
import { Image } from '@rocco/components/image';
import type { Currency, ProductItem } from '../../type/types';
import { getProductColumnClass, formatPrice } from '../../util/utils';
import { cn } from '@rocco/index';
import { PROPOSAL_TYPO_CLASS } from './proposal-styles';
import { ProductNameComponent } from './ProductNameComponent';

interface ProductRowProps {
    productItem: ProductItem;
    currency: Currency;
    isMobile: boolean;
    /** When false, hides the tax column (used when all line items have the same tax rate > 0) */
    showTaxColumn?: boolean;
    /** When false, hides the discount column (used when total discount is 0) */
    showDiscountColumn?: boolean;
}

interface ImageComponentProps {
    imageUrl?: string;
    name: string;
}

/** Product image thumbnail for the proposal product table. */
const ImageComponent = ({ imageUrl, name }: ImageComponentProps) =>
    imageUrl ? (
        <Image
            overrideSrc={imageUrl}
            src={imageUrl}
            alt={name}
            className="h-15 w-15 rounded-md"
        />
    ) : null;

/**
 * Product row component for displaying product information in a grid layout
 */
export const ProductRow = ({
    productItem,
    currency,
    isMobile,
    showTaxColumn = true,
    showDiscountColumn = true,
}: ProductRowProps) => {
    // for mobile and non-mobile
    const divClass =
        'rounded-md bg-[#f6f6f6] backdrop-blur-[30px] print:break-inside-avoid';
    if (isMobile) {
        // never show tax column on mobile

        // for mobile second row, such as QTY, UNIT PRICE, DISCOUNT, PRICE, all in %
        const columnWidths = {
            qty: 19,
            unitPrice: 21,
            discount: 30,
            price: 30,
        };
        if (!showDiscountColumn) {
            columnWidths.qty += columnWidths.discount;
            columnWidths.discount = 0;
        }

        return (
            <div className={cn(divClass, 'pt-2.25 px-[0.5rem] pb-[1.4rem]')}>
                {/* First row: Product info with description */}
                <div className="mb-[0.5rem] flex flex-row gap-2">
                    <ImageComponent
                        imageUrl={productItem.imageUrl}
                        name={productItem.name}
                    />
                    <ProductNameComponent
                        name={productItem.name}
                        description={productItem.description}
                    />
                </div>

                {/* Second row: Qty, Unit Price, Discount, Tax, Price */}
                <div className="mt-[0.3rem] flex flex-row items-center">
                    <div
                        className="flex flex-col"
                        style={{ width: columnWidths.qty + '%' }}
                    >
                        <span
                            className={cn(
                                PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_HEADER,
                                'mb-1 uppercase',
                            )}
                        >
                            Qty
                        </span>
                        <span
                            className={cn(
                                // PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_ROW_QTY
                                // workaround for mobile; font-semibold -> font-medium
                                'overflow-hidden text-ellipsis font-display text-[0.875rem] font-medium leading-[0.8572rem] tracking-[-0.0088rem] text-[#9B9B9B] md:font-sans md:text-[0.6614rem] md:font-normal md:leading-normal md:tracking-[unset] md:text-[#6E6E6E]',
                            )}
                        >
                            {productItem.quantity}
                        </span>
                    </div>
                    <div
                        className="flex flex-col text-right"
                        style={{ width: columnWidths.unitPrice + '%' }}
                    >
                        <span
                            className={cn(
                                PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_HEADER,
                                'mb-[0.5rem] uppercase',
                            )}
                        >
                            Unit Price
                        </span>
                        <span
                            className={cn(
                                // PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_ROW_UNIT_PRICE
                                // workaround for mobile; font-semibold -> font-medium
                                'overflow-hidden text-ellipsis font-display text-[0.875rem] font-medium leading-[0.8572rem] tracking-[-0.0088rem] text-[#9B9B9B] md:text-[0.8572rem] md:font-[450] md:tracking-[-0.0086rem] md:text-[#7E7E7E]',
                            )}
                        >
                            {formatPrice(productItem.unitPrice, currency)}
                        </span>
                    </div>
                    {showDiscountColumn && (
                        <div
                            className="flex flex-col text-right"
                            style={{ width: columnWidths.discount + '%' }}
                        >
                            <span
                                className={cn(
                                    PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_HEADER,
                                    'mb-[0.5rem] uppercase',
                                )}
                            >
                                Discount
                            </span>
                            <span
                                className={cn(
                                    // PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_ROW_UNIT_PRICE
                                    // workaround for mobile; font-semibold -> font-medium
                                    'overflow-hidden text-ellipsis font-display text-[0.875rem] font-medium leading-[0.8572rem] tracking-[-0.0088rem] text-[#9B9B9B] md:text-[0.8572rem] md:font-[450] md:tracking-[-0.0086rem] md:text-[#7E7E7E]',
                                )}
                            >
                                {productItem.discountApplied
                                    ? formatPrice(
                                          productItem.discountAmountPerUnit,
                                          currency,
                                      )
                                    : '-'}
                            </span>
                        </div>
                    )}
                    {/* {showTaxColumn && (
                        <div
                            className="flex flex-col text-right"
                            style={{ width: regularColWidth }}
                        >
                            <span
                                className={cn(
                                    PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_HEADER,
                                    'mb-1 uppercase'
                                )}
                            >
                                Tax
                            </span>
                            <span
                                className={cn(
                                    PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_ROW_UNIT_PRICE
                                )}
                            >
                                {(productItem.taxRate * 100).toFixed(0)}%
                            </span>
                        </div>
                    )} */}
                    <div
                        className="flex flex-col pr-[0.13rem] text-right"
                        style={{ width: columnWidths.price + '%' }}
                    >
                        <span
                            className={cn(
                                PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_HEADER,
                                'mb-[0.5rem] uppercase',
                            )}
                        >
                            Price
                        </span>
                        <span
                            className={cn(
                                PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_ROW_PRICE,
                            )}
                        >
                            {formatPrice(productItem.totalPrice, currency)}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={cn('flex', divClass)}>
            <div
                className={cn(
                    getProductColumnClass('title'),
                    'flex flex-row gap-2 p-2',
                )}
            >
                <ImageComponent
                    imageUrl={productItem.imageUrl}
                    name={productItem.name}
                />
                <ProductNameComponent
                    name={productItem.name}
                    description={productItem.description}
                />
            </div>
            <div
                className={cn(
                    PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_ROW_QTY,
                    'mt-8',
                    getProductColumnClass('qty'),
                )}
            >
                {productItem.quantity}
            </div>
            <div className={getProductColumnClass('qtySpace')} />
            <div className={getProductColumnClass('unitPriceSpace')} />
            <div
                className={cn(
                    PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_ROW_UNIT_PRICE,
                    'mt-8 print:text-base',
                    getProductColumnClass('unitPrice'),
                )}
            >
                <span>{formatPrice(productItem.unitPrice, currency)}</span>
            </div>
            {showDiscountColumn && (
                <div
                    className={cn(
                        PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_ROW_UNIT_PRICE,
                        'mt-8 print:text-base',
                        getProductColumnClass('discount'),
                    )}
                >
                    {productItem.discountApplied
                        ? formatPrice(
                              productItem.discountAmountPerUnit,
                              currency,
                          )
                        : '-'}
                </div>
            )}
            {showTaxColumn && (
                <div
                    className={cn(
                        PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_ROW_UNIT_PRICE,
                        'mt-8 print:text-base',
                        getProductColumnClass('tax'),
                    )}
                >
                    {(productItem.taxRate * 100).toFixed(0)}%
                </div>
            )}
            <div
                className={cn(
                    PROPOSAL_TYPO_CLASS.PRODUCT_TABLE_ROW_PRICE,
                    'mt-8 print:text-base',
                    getProductColumnClass('price'),
                )}
            >
                {formatPrice(productItem.totalPrice, currency)}
            </div>
        </div>
    );
};

export default ProductRow;
